import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import {
    Share2,
    Clock,
    User,
    Calendar,
    Tag,
    Eye,
    Facebook,
    Twitter,
    Linkedin,
    Copy,
    CheckCircle,
    Star,
    BookOpen,
    ChevronRight,
    Bookmark as BookmarkIcon,
    Heart as HeartIcon
} from 'lucide-react';

import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { useGetBlogBySlugQuery } from '../store/slices/apiSlice';
import { API_BASE_URL } from '../config/api';

const BlogDetails = () => {
    const { slug } = useParams();
    const [isVisible, setIsVisible] = useState(false);

    // Set page metadata
    usePageMetadata('blogDetails');
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [copied, setCopied] = useState(false);

    // Fetch blog data from API
    const { data: blogData, isLoading, error } = useGetBlogBySlugQuery(slug || '');

    useEffect(() => {
        setIsVisible(true);
        window.scrollTo(0, 0);
    }, [slug]);

    // Get blog data from API or use fallback
    const blogDataFromAPI = blogData?.data;

    // Transform API data to match component structure
    const blogPost = blogDataFromAPI ? {
        id: blogDataFromAPI.id || 0,
        title: blogDataFromAPI.title || 'Untitled',
        excerpt: blogDataFromAPI.desc || 'No description available',
        content: blogDataFromAPI.content || '<p>Content not available</p>',
        author: {
            name: blogDataFromAPI.author || 'Unknown Author',
            role: "Author",
            bio: "Expert author with extensive knowledge in the field.",
            image: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80",
            social: {
                twitter: "@author",
                linkedin: "author-profile",
                website: "author.com"
            }
        },
        category: blogDataFromAPI.category || 'General',
        readTime: `${blogDataFromAPI.readTime || 5} min read`,
        publishDate: blogDataFromAPI.publishedAt || new Date().toISOString(),
        lastUpdated: blogDataFromAPI.updated_at || new Date().toISOString(),
        image: blogDataFromAPI.image &&
            blogDataFromAPI.image !== `${API_BASE_URL}/null` &&
            blogDataFromAPI.image !== 'null' &&
            blogDataFromAPI.image.trim() !== ''
            ? blogDataFromAPI.image
            : "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80",
        views: blogDataFromAPI.views || 0,
        likes: blogDataFromAPI.likes || 0,
        featured: blogDataFromAPI.featured || false,
        tags: Array.isArray(blogDataFromAPI.tags) ? blogDataFromAPI.tags : []
    } : null;


    const handleShare = (platform: string) => {
        if (!blogPost) return;

        const url = window.location.href;
        const title = blogPost.title || 'Check out this article';

        switch (platform) {
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`);
                break;
            case 'facebook':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
                break;
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
                break;
            case 'copy':
                navigator.clipboard.writeText(url).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                }).catch(() => {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = url;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                });
                break;
        }
        setShowShareMenu(false);
    };


    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-xl text-gray-600">Loading blog post...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Error state
    if (error || !blogPost) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {error ? 'Error Loading Blog Post' : 'Blog Post Not Found'}
                        </h2>
                        <p className="text-gray-600 mb-4">
                            {error
                                ? 'There was an error loading the blog post. Please try again later.'
                                : 'The blog post you\'re looking for doesn\'t exist or has been removed.'
                            }
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/blogs"
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Back to Blogs
                            </Link>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            {/* Simple Hero Section */}
            <section className="relative py-8 px-4 sm:px-6 bg-white border-b border-gray-200">
                <div className="container mx-auto max-w-4xl">
                    <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        {/* Breadcrumb */}
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                            <Link to="/blogs" className="hover:text-gray-700 transition-colors duration-200">
                                Blogs
                            </Link>
                            <ChevronRight className="w-4 h-4" />
                            <span className="text-gray-700">Article</span>
                        </div>

                        {/* Article Meta */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4">
                            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-xs sm:text-sm font-medium w-fit border border-gray-200">
                                {blogPost.category}
                            </span>
                            <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm">
                                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span>{formatDateUtil(blogPost.publishDate)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span>{blogPost.readTime}</span>
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                            {blogPost.title}
                        </h1>

                        {/* Excerpt */}
                        <p className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed">
                            {blogPost.excerpt}
                        </p>

                        {/* Author Info */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{blogPost.author.name}</p>
                                    <p className="text-gray-500 text-xs sm:text-sm">{blogPost.author.role}</p>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2 sm:gap-3">
                                <button
                                    onClick={() => setIsBookmarked(!isBookmarked)}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${isBookmarked
                                        ? 'bg-gray-700 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    <BookmarkIcon className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setIsLiked(!isLiked)}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${isLiked
                                        ? 'bg-red-500 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    <HeartIcon className="w-5 h-5" />
                                </button>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowShareMenu(!showShareMenu)}
                                        className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center transition-all duration-200"
                                    >
                                        <Share2 className="w-5 h-5" />
                                    </button>

                                    {/* Share Menu */}
                                    {showShareMenu && (
                                        <div className="absolute right-0 top-12 bg-white rounded-xl shadow-lg border border-gray-200 p-4 min-w-[200px] z-50">
                                            <div className="space-y-2">
                                                <button
                                                    onClick={() => handleShare('twitter')}
                                                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <Twitter className="w-4 h-4 text-blue-400" />
                                                    <span className="text-gray-700">Twitter</span>
                                                </button>
                                                <button
                                                    onClick={() => handleShare('facebook')}
                                                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <Facebook className="w-4 h-4 text-blue-600" />
                                                    <span className="text-gray-700">Facebook</span>
                                                </button>
                                                <button
                                                    onClick={() => handleShare('linkedin')}
                                                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <Linkedin className="w-4 h-4 text-blue-700" />
                                                    <span className="text-gray-700">LinkedIn</span>
                                                </button>
                                                <button
                                                    onClick={() => handleShare('copy')}
                                                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    {copied ? (
                                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                                    ) : (
                                                        <Copy className="w-4 h-4 text-gray-500" />
                                                    )}
                                                    <span className="text-gray-700">
                                                        {copied ? 'Copied!' : 'Copy Link'}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 md:gap-12">
                        {/* Article Content */}
                        <div className="lg:col-span-2 blog-content">
                            {/* Featured Image */}
                            <div className="mb-8 sm:mb-10 md:mb-12">
                                <img
                                    src={blogPost.image}
                                    alt={blogPost.title}
                                    className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-contain bg-gray-100 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl"
                                />
                            </div>

                            {/* Article Stats */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 p-4 sm:p-6 bg-gray-50 rounded-lg sm:rounded-xl gap-4 sm:gap-0">
                                <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                                        <span className="font-semibold text-sm sm:text-base">{blogPost.views?.toLocaleString() || 0}</span>
                                        <span className="text-xs sm:text-sm">views</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                        <span className="font-semibold text-sm sm:text-base">{blogPost.likes || 0}</span>
                                        <span className="text-xs sm:text-sm">likes</span>
                                    </div>
                                </div>
                                <div className="text-xs sm:text-sm text-gray-500">
                                    Last updated: {blogPost.lastUpdated ? formatDateUtil(blogPost.lastUpdated) : 'Unknown'}
                                </div>
                            </div>

                            {/* Article Content */}
                            <div
                                className="prose prose-sm sm:prose-base md:prose-lg max-w-none mb-8 sm:mb-10 md:mb-12 prose-headings:text-gray-900 prose-h1:text-2xl sm:prose-h1:text-3xl prose-h2:text-xl sm:prose-h2:text-2xl prose-h3:text-lg sm:prose-h3:text-xl prose-p:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-blockquote:text-gray-600 prose-code:text-gray-800 prose-pre:bg-gray-100"
                                dangerouslySetInnerHTML={{ __html: blogPost.content }}
                            />

                            {/* Tags */}
                            <div className="mb-8 sm:mb-10 md:mb-12">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                                    <Tag className="w-4 h-4 sm:w-5 sm:h-5" />
                                    Tags
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {Array.isArray(blogPost.tags) && blogPost.tags.length > 0 ? (
                                        blogPost.tags.map((tag: string, index: number) => (
                                            <span
                                                key={index}
                                                className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-xs sm:text-sm font-medium hover:bg-gray-200 transition-colors duration-200 cursor-pointer border border-gray-200"
                                            >
                                                {tag}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-gray-500 text-xs sm:text-sm">No tags available</span>
                                    )}
                                </div>
                            </div>


                        </div>

                        {/* Sidebar */}
                        <div className="space-y-8 blog-sidebar">
                            {/* Table of Contents */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <BookOpen className="w-5 h-5" />
                                    Table of Contents
                                </h3>
                                <nav className="space-y-2">
                                    <a href="#technology" className="block text-blue-600 hover:text-blue-700 transition-colors duration-300 text-sm">
                                        1. Technology Integration
                                    </a>
                                    <a href="#esg" className="block text-blue-600 hover:text-blue-700 transition-colors duration-300 text-sm">
                                        2. Sustainable and ESG Investing
                                    </a>
                                    <a href="#emerging" className="block text-blue-600 hover:text-blue-700 transition-colors duration-300 text-sm">
                                        3. Emerging Markets Opportunities
                                    </a>
                                    <a href="#digital" className="block text-blue-600 hover:text-blue-700 transition-colors duration-300 text-sm">
                                        4. Digital Assets and Cryptocurrency
                                    </a>
                                    <a href="#personalization" className="block text-blue-600 hover:text-blue-700 transition-colors duration-300 text-sm">
                                        5. Personalization and Customization
                                    </a>
                                </nav>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* Simple CTA Section */}
            <section className="py-12 px-4 sm:px-6 bg-gray-50">
                <div className="container mx-auto max-w-4xl text-center">
                    <div className="inline-flex items-center gap-2 bg-gray-100 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium text-gray-600 mb-4 sm:mb-6 border border-gray-200">
                        <Star className="w-3 h-3 sm:w-4 sm:h-4" />
                        Explore More
                    </div>

                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                        Ready to Start Investing?
                    </h2>

                    <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
                        Join thousands of investors who are already building wealth through our platform.
                        Start your investment journey today.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                        <Link
                            to="/projects"
                            className="px-6 sm:px-8 py-3 sm:py-4 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-colors duration-200 text-sm sm:text-base"
                        >
                            Explore Investments
                        </Link>
                        <Link
                            to="/blogs"
                            className="px-6 sm:px-8 py-3 sm:py-4 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 text-sm sm:text-base"
                        >
                            Read More Articles
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default BlogDetails; 